/**
 * Per-route SEO configuration.
 *
 * Centralised so every container points at the same single source of
 * truth for its title / description / canonical / noIndex / schema.
 *
 * Adding or renaming a route?
 *   1. Add an entry here.
 *   2. Have the container call ``<SEO {...routeSeo.<key>} />``.
 *   3. The build-time sitemap generator reads this file too — update
 *      it if a public route is added or removed.
 *
 * The shape of each entry matches the SEO component's props.
 */

import {
  organizationSchema,
  serviceSchema,
  faqPageSchema,
  foundersSchema,
  reviewsSchema,
} from "./schema";
import { FAQPageQuestions, testimonials } from "../../constants/descriptions";

// A short blurb used in the homepage description AND as a generic
// fallback elsewhere.  Keep <=160 chars so Google doesn't truncate.
const HOME_DESC =
  "Generate a professional Texas property tax Full Report in minutes. Comparable sales, equity analysis, and ARB-ready evidence packets — keep what you save.";

const routeSeo = {
  home: {
    title:
      "Protest Your Texas Property Taxes | DIY Reports That Get Results",
    description: HOME_DESC,
    canonical: "/",
    schema: [
      organizationSchema(),
      serviceSchema(),
      reviewsSchema(testimonials),
    ],
  },

  verify: {
    title:
      "Free Property Tax Estimate | Texas Property Tax Protest",
    description:
      "Enter your Texas property address for a free estimate of how much you may have over-paid in property tax — and download an ARB-ready Full Report.",
    canonical: "/verify",
    schema: serviceSchema(),
  },

  faq: {
    title: "Property Tax Protest FAQ | How It Works | PPT",
    description:
      "Answers to common Texas property tax protest questions: deadlines, evidence, county appraisal districts, and how the report process works.",
    canonical: "/faq",
    schema: faqPageSchema(
      // ``content`` is JSX so the FAQ page's full text isn't directly
      // serialisable — declare a ``plainTextAnswer`` per row in
      // descriptions.js to opt a question into the rich result.
      // Until that lands, the schema falls back to a "see /faq"
      // pointer per row, which keeps the FAQPage validity intact
      // without overclaiming what's on-page.
      (FAQPageQuestions && FAQPageQuestions.rows) || [],
    ),
  },

  about: {
    title:
      "About Protesting Property Taxes | Bob & Melinda McKenna",
    description:
      "Founded by 20-year Austin real-estate brokers Bob and Melinda McKenna. We turn comparable sales and equity data into protest-ready evidence for Texas homeowners.",
    canonical: "/about",
    schema: foundersSchema(),
  },

  pricing: {
    title:
      "Pricing | Flat-Fee Property Tax Protest Reports for Texas Homeowners",
    description:
      "$99 per year, flat — keep 100% of what you save on your property taxes. No percentage cuts, no surprise fees. See what's included.",
    canonical: "/pricing",
  },

  agents: {
    title:
      "Real Estate Agent Partners | Give Clients Value Every Year",
    description:
      "Partner with us to give clients a free annual property-tax touchpoint: stay top-of-mind after closing, help homeowners save money, and earn referral income.",
    canonical: "/agents",
    schema: serviceSchema(),
  },

  privacy: {
    title: "Privacy Policy | Protesting Property Taxes",
    description:
      "How Protesting Property Taxes collects, uses, and stores customer data — including property records, payment information, and email correspondence.",
    canonical: "/privacy",
  },

  terms: {
    title: "Terms and Conditions | Protesting Property Taxes",
    description:
      "Service terms, money-back guarantee details, and acceptable use of Protesting Property Taxes' Texas property-tax-Full Reports.",
    canonical: "/terms-and-conditions",
  },

  preOrderSubmit: {
    // Title kept under the 70-char SERP cap (Google truncates the
    // rest in results) — see the routeSeo test that pins this.
    title: "Free Pre-order Estimate | Protesting Property Taxes",
    description:
      "Submit your Texas property address to get a free pre-order estimate before this year's appraisal data is finalized.",
    canonical: "/pre-order/submit",
  },

  // ── Customer-private routes ───────────────────────────────────
  // Each carries an order UUID in the URL.  Forwarded links can
  // surface in third-party places (email clients, screenshots) that
  // crawlers have access to, so noIndex is required defence-in-
  // depth alongside the Disallow rules in /robots.txt.

  selectReport: {
    title: "Choose Your Report | Protesting Property Taxes",
    description:
      "Select the report tier for your Texas property tax protest.",
    canonical: "/",
    noIndex: true,
  },

  checkout: {
    title: "Checkout | Protesting Property Taxes",
    description:
      "Complete payment for your property tax Full Report.",
    canonical: "/",
    noIndex: true,
  },

  subscribe: {
    title: "Subscribe | Protesting Property Taxes",
    description:
      "Subscribe to yearly Texas property tax Full Reports.",
    canonical: "/",
    noIndex: true,
  },

  retrieveReport: {
    title: "Your Report | Protesting Property Taxes",
    description: "Download your property tax Full Report.",
    canonical: "/",
    noIndex: true,
  },

  refund: {
    title: "Refund Status | Protesting Property Taxes",
    description: "Look up your Protesting Property Taxes order to request a refund.",
    canonical: "/",
    noIndex: true,
  },

  refundStatus: {
    title: "Refund Status | Protesting Property Taxes",
    description: "Status of your Protesting Property Taxes refund request.",
    canonical: "/",
    noIndex: true,
  },

  cancel: {
    title: "Cancel Subscription | Protesting Property Taxes",
    description: "Cancel your yearly Protesting Property Taxes subscription.",
    canonical: "/",
    noIndex: true,
  },

  // Landing page for expired/invalid signed report links.  The
  // canonical matches the App.js path so the routes-drift test can
  // classify it as an intentional noindex route (like /login).
  reportLink: {
    title: "Get a Fresh Report Link | Protesting Property Taxes",
    description: "Request a fresh link to your property tax report.",
    canonical: "/report-link",
    noIndex: true,
  },

  // Guest cancel/skip email-confirmation page.
  subscriptionConfirm: {
    title: "Confirm Your Request | Protesting Property Taxes",
    description: "Confirm your subscription cancellation request.",
    canonical: "/subscription/confirm",
    noIndex: true,
  },

  success: {
    title: "Payment Successful | Protesting Property Taxes",
    description: "Thank you for your purchase.",
    canonical: "/",
    noIndex: true,
  },

  cardUpdated: {
    title: "Card Updated | Protesting Property Taxes",
    description: "Your payment method has been updated.",
    canonical: "/",
    noIndex: true,
  },

  preOrderComplete: {
    title: "Pre-order Complete | Protesting Property Taxes",
    description: "Your pre-order has been submitted.",
    canonical: "/",
    noIndex: true,
  },

  login: {
    title: "Sign In | Protesting Property Taxes",
    description:
      "Sign in to your account to view your Texas property tax reports, savings, and order history.",
    canonical: "/login",
    noIndex: true,
  },

  dashboard: {
    title: "Your Dashboard | Protesting Property Taxes",
    description:
      "Your property tax reports, estimated savings, filing instructions, subscriptions, and account details.",
    canonical: "/dashboard",
    noIndex: true,
  },

  // Shown when a user picks an address we don't cover yet (external-
  // geocoder fallback).  noIndex — it only makes sense mid-flow with a
  // selected address in state; there's no standalone SEO value.
  coverage: {
    title: "Coming Soon to Your Area | Protesting Property Taxes",
    description:
      "We don't generate property tax protest reports for this area yet — tell us where you are and we'll notify you when we expand.",
    canonical: "/coverage",
    noIndex: true,
  },

  // Owner-authored market-value report homepage (national / neutral lane).
  // Indexable marketing page with its own lane-neutral chrome.
  "market-report": {
    title: "Owner-Authored Market-Value Report | PXN Residential",
    description:
      "Build a market-value report for your home: review recent comparable sales, keep the ones you find comparable, and state your own opinion of value in a clean, shareable report.",
    canonical: "/market-report",
  },

  // National-expansion intake. noIndex — it only makes sense mid-flow with a
  // selected (out-of-Texas) address in Redux state; no standalone SEO value.
  "national-intake": {
    title: "Build Your Market-Value Report | PXN Residential",
    description:
      "Assemble comparable-sales evidence and state your own opinion of your home's market value.",
    canonical: "/national-intake",
    noIndex: true,
  },

  // National-lane legal pages, in the lane-neutral chrome. The terms are
  // PXN Residential LLC's own (the Texas Contestation Report terms don't
  // cover national purchases); the privacy policy is the shared site-wide
  // one rendered without the Texas chrome.
  "national-terms": {
    title: "Terms of Service | PXN Residential",
    description:
      "Terms of service for PXN Residential's owner-authored market-value and assessment evidence reports.",
    canonical: "/national/terms",
  },
  "national-privacy": {
    title: "Privacy Policy | PXN Residential",
    description:
      "How we collect, use, and protect your personal information.",
    canonical: "/national/privacy",
  },

  submit: {
    title: "Submit Property Information | Protesting Property Taxes",
    description: "Submit additional details about your property.",
    canonical: "/",
    noIndex: true,
  },
};

// Public routes whose URLs should appear in the sitemap.  Centralised
// here so the build-time sitemap generator (scripts/build-sitemap.js)
// stays in lockstep with what the router exposes.  Each value is the
// site-relative path WITHOUT a trailing slash (except root).
//
// SEO landing-page routes (county pages, state hub, evergreen
// non-county pages) are appended at the end so they don't reorder
// the marketing routes.
export const PUBLIC_ROUTES = [
  "/",
  "/about",
  "/faq",
  "/pricing",
  "/privacy",
  "/terms-and-conditions",
  "/verify",
  "/market-report",
  "/national/terms",
  "/national/privacy",
  "/pre-order/submit",
  "/agents",
  // SEO landing pages — evergreen + state hub
  "/texas-property-tax-protest",
  "/how-to-protest-property-taxes",
  "/property-tax-protest-deadlines-2026",
  "/property-tax-protest-evidence",
  "/texas-homestead-exemption-guide",
  "/property-tax-glossary",
  "/texas-property-tax-timeline-2026",
  "/property-tax-protest-companies-comparison",
  // SEO landing pages — county
  "/bexar-county-property-tax-protest",
  "/collin-county-property-tax-protest",
  "/dallas-county-property-tax-protest",
  "/denton-county-property-tax-protest",
  "/fort-bend-county-property-tax-protest",
  "/harris-county-property-tax-protest",
  "/hays-county-property-tax-protest",
  "/montgomery-county-property-tax-protest",
  "/tarrant-county-property-tax-protest",
  "/travis-county-property-tax-protest",
  "/williamson-county-property-tax-protest",
];

// Private route prefixes — sitemap excludes these AND robots.txt
// disallows them.  Source of truth lives here so updating one path
// updates both surfaces.
export const PRIVATE_ROUTE_PREFIXES = [
  "/select-report/",
  "/checkout/",
  "/subscribe/",
  "/report/",
  "/refund",
  "/cancel",
  "/success",
  "/card-updated",
  "/preorder-complete",
  "/submit",
  "/dashboard",
];

export default routeSeo;
